/**
 * @flowr/sdk-recorder-local
 * --------------------------------------------------------------------------
 * Internal / dev-only build of the FlowR recorder.
 *
 * Persists recordings to `localStorage` only. There is no backend, no
 * auth, no sync, and no token. Intended for:
 *   - local prototyping while iterating on the recorder UX,
 *   - automated tests that need a recorder UI without spinning up
 *     Supabase fixtures,
 *   - bundled demos / Storybook examples.
 *
 * UI:
 *   - The bubble + recorder panel + context menu / instruction prompt are
 *     all rendered by `@flowr/sdk-ui` so this package contains zero
 *     bespoke UI markup or styles. Future cloud / extension consumers
 *     mount the same primitives with their own state and persistence.
 *
 * Public API mirrors `@celestialsynthesis/flowr-sdk-recorder` so consumers can swap the
 * import without changing call sites:
 *
 *   const handle = FlowR.recorderLocal();
 *   await handle.startRecording({ title: "Onboarding" });
 *   const rec = await handle.stopRecording(); // saved to localStorage
 *   const all = await handle.listRecordings();
 *
 * Storage key: `flowr-recorder-local:recordings` — JSON array of
 * `Recording` objects keyed by `id`. Replace the array atomically.
 *
 * --------------------------------------------------------------------------
 * KNOWN ASYMMETRIES vs `@celestialsynthesis/flowr-sdk-recorder`
 * --------------------------------------------------------------------------
 * The two packages target the same recorder behavior but currently
 * diverge in these areas. When extending one, consider whether the
 * other should follow:
 *
 *  - Public handle: `clearAll()` exists here but not in `sdk-recorder`.
 *  - Recording behavior: focus hints (`onDocumentFocusInCapture`/`Out`),
 *    context-menu element picker (`onDocumentContextMenuCapture`,
 *    `pickElementOnce`), and instruction-step authoring helpers
 *    are enabled here but not in the hosted recorder configuration.
 *  - Storage: `chrome.storage.local`-style adapter not used; pure
 *    `localStorage` via `createSafeStorage()` from sdk-core.
 */

import type { Recording, RecordingVisibility, SdkTheme, Step } from "@flowr/sdk-core";
import { FLOWR_DEFAULT_ICON_DATA_URL } from "@flowr/sdk-core";
import { createRecorderKernel } from "@flowr/sdk-recorder-kernel";
import { createLocalRecordingStore } from "./local-recording-store";

// Re-export shared UI primitives so playgrounds and demos can compose
// the same panel/overlay into their own host-page layout. The package
// inlines @flowr/sdk-ui via tsup `noExternal`, so consumers see one
// public entry point and never need to install @flowr/sdk-ui directly.
export {
  createRecorderOverlay,
  createRecorderPanel,
  type RecorderOverlayHandle,
  type RecorderPanelHandle,
  type RecorderPanelHandlers,
  type RecorderPanelOptions,
  type RecorderPanelState,
  type RecorderPanelStatus,
} from "@flowr/sdk-ui";

export type RecorderLocalEvent =
  | { type: "ready" }
  | { type: "recording-started"; recording: Recording }
  | { type: "step-captured"; recording: Recording; step: Step }
  | { type: "step-updated"; recording: Recording; step: Step }
  | { type: "settings-updated"; captureScreenshots: boolean }
  | { type: "recording-saved"; recording: Recording }
  | { type: "recording-loaded"; recording: Recording }
  | { type: "replay-start"; recording: Recording }
  | { type: "replay-step"; index: number; step: Step }
  | { type: "replay-complete" }
  | { type: "error"; error: Error };

export type RecorderLocalOptions = {
  /** Optional analytics-only end-user id surfaced on persisted rows. */
  userId?: string;
  /** Capture screenshots per step (lazy-loads html-to-image). */
  screenshots?: boolean;
  /** Bubble dock position. Default: "bottom-right". */
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  iconUrl?: string;
  panelCssText?: string;
  theme?: SdkTheme;
  /** Override the localStorage key (useful for isolated test fixtures). */
  storageKey?: string;
  /**
   * When true (default), left-clicks on the host page are intercepted
   * during recording and the user is prompted for an instruction before
   * the click step is saved — matching the FlowR extension's behavior.
   * Set to `false` to record clicks passively without any prompt.
   */
  interceptClicks?: boolean;
  /** Reload matching start pages before replay. Default: true. */
  refreshBeforeReplay?: boolean;
};

export type RecorderLocalHandle = {
  open(): void;
  close(): void;
  startRecording(opts?: { title?: string; visibility?: RecordingVisibility }): Promise<Recording>;
  stopRecording(): Promise<Recording | null>;
  setCaptureScreenshots(value: boolean): void;
  setVisibility(visibility: RecordingVisibility): Recording | null;
  getCurrentRecording(): Recording | null;
  loadRecording(id: string): Promise<Recording>;
  listRecordings(): Promise<Recording[]>;
  deleteRecording(id: string): Promise<void>;
  /** Wipe all locally-stored recordings. */
  clearAll(): Promise<void>;
  replay(idOrRecording: string | Recording): Promise<void>;
  destroy(): void;
  on<E extends RecorderLocalEvent["type"]>(
    type: E,
    listener: (event: Extract<RecorderLocalEvent, { type: E }>) => void,
  ): () => void;
};

const DEFAULT_STORAGE_KEY = "flowr-recorder-local:recordings";

// ---- Recorder ---------------------------------------------------------------

export const recorderLocal = (opts: RecorderLocalOptions = {}): RecorderLocalHandle => {
  const listeners = new Map<RecorderLocalEvent["type"], Set<(e: RecorderLocalEvent) => void>>();
  const emit = (event: RecorderLocalEvent): void => {
    listeners.get(event.type)?.forEach((l) => l(event));
  };

  const kernel = createRecorderKernel({
    store: createLocalRecordingStore(opts.storageKey ?? DEFAULT_STORAGE_KEY),
    bubble: {
      defaultIconDataUrl: FLOWR_DEFAULT_ICON_DATA_URL,
      iconUrl: opts.iconUrl,
      panelCssText: opts.panelCssText,
      theme: opts.theme,
      position: opts.position,
      tooltip: "Open FlowR recorder (local)",
    },
    captureScreenshots: opts.screenshots === true,
    interceptClicks: opts.interceptClicks !== false,
    refreshBeforeReplay: opts.refreshBeforeReplay !== false,
    userId: opts.userId,
    authoring: {
      enableContextMenuAuthoring: true,
      enableInputFocusHints: true,
    },
    hooks: {
      onReady: () => emit({ type: "ready" }),
      onRecordingStarted: (recording) => emit({ type: "recording-started", recording }),
      onStepCaptured: (recording, step) => emit({ type: "step-captured", recording, step }),
      onStepUpdated: (recording, step) => emit({ type: "step-updated", recording, step }),
      onCaptureScreenshotsChanged: (captureScreenshots) =>
        emit({ type: "settings-updated", captureScreenshots }),
      onRecordingSaved: (recording) => emit({ type: "recording-saved", recording }),
      onRecordingLoaded: (recording) => emit({ type: "recording-loaded", recording }),
      onReplayStart: (recording) => emit({ type: "replay-start", recording }),
      onReplayStep: (index, step) => emit({ type: "replay-step", index, step }),
      onReplayComplete: () => emit({ type: "replay-complete" }),
      onError: (error) => emit({ type: "error", error }),
    },
  });

  const panelBinding = kernel.attachSdkUiPanel({ theme: opts.theme });

  const handle: RecorderLocalHandle = {
    open: () => kernel.open(),
    close: () => kernel.close(),

    async startRecording(startOpts) {
      return kernel.startRecording(startOpts);
    },

    async stopRecording() {
      return kernel.stopRecording();
    },

    setCaptureScreenshots(value) {
      kernel.setCaptureScreenshots(value);
    },

    setVisibility(visibility) {
      return kernel.setVisibility(visibility);
    },

    getCurrentRecording: () => kernel.getCurrentRecording(),

    async loadRecording(id) {
      return kernel.loadRecording(id);
    },

    async listRecordings() {
      return kernel.listRecordings();
    },

    async deleteRecording(id) {
      return kernel.deleteRecording(id);
    },

    async clearAll() {
      return kernel.clearAll();
    },

    async replay(idOrRec) {
      return kernel.replay(idOrRec);
    },

    destroy: () => {
      panelBinding.destroy();
      kernel.destroy();
      listeners.clear();
    },

    on(type, listener) {
      let set = listeners.get(type);
      if (!set) {
        set = new Set();
        listeners.set(type, set);
      }
      set.add(listener as (e: RecorderLocalEvent) => void);
      return () => set!.delete(listener as (e: RecorderLocalEvent) => void);
    },
  };

  return handle;
};
